import {isPlatformBrowser} from '@angular/common';
import {Inject, Injectable, NgZone, PLATFORM_ID, signal} from '@angular/core';
import {App, type AppInfo} from '@capacitor/app';
import {Capacitor, type PluginListenerHandle} from '@capacitor/core';
import {ToastController, type ToastOptions} from '@ionic/angular';
import {TranslateService} from '@ngx-translate/core';
import {SmConfirmationDialogService} from '../components/sm-confirmation-dialog/sm-confirmation-dialog.service';
import {AppUpdate, type AppUpdatePlugin, type AppUpdateState} from './app-update.plugin';
import {isStagingEnvironment} from '../../auth/native-auth-config';

const APP_UPDATE_LOG_SOURCE = '[app-update]';
const UPDATE_CHECK_TIMEOUT_MS = 15_000;
const APP_INFO_TIMEOUT_MS = 3_000;
const TOAST_OPERATION_TIMEOUT_MS = 5_000;
const DOWNLOAD_PROGRESS_ANNOUNCEMENT_STEP_PERCENT = 10;

export interface AppUpdateDownloadProgress {
  percentage: number | null;
  announcedPercentage: number | null;
}

@Injectable({providedIn: 'root'})
export class AppUpdateService {
  private readonly mutableDownloadProgress = signal<AppUpdateDownloadProgress | null>(null);
  readonly downloadProgress = this.mutableDownloadProgress.asReadonly();

  private plugin?: AppUpdatePlugin;
  private stateListener?: PluginListenerHandle;
  private stateListenerRegistration?: Promise<void>;
  private appStateListener?: PluginListenerHandle;
  private activeToast?: HTMLIonToastElement;
  private toastOperation: Promise<void> = Promise.resolve();
  private toastGeneration = 0;
  private activeCheck?: Promise<AppUpdateState | undefined>;
  private restartPrompt?: Promise<boolean>;
  private initialized = false;
  private lifecycleVersion = 0;
  private postponed = false;
  private restartPromptPostponed = false;
  private updateFlowInProgress = false;
  private updateStarted = false;
  private manualCheckInProgress = false;
  private manualQuery?: Promise<AppUpdateState | undefined>;
  private downloadProgressFinished = false;

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private readonly ngZone: NgZone,
    private readonly toastController: ToastController,
    private readonly translateService: TranslateService,
    private readonly confirmationDialogService: SmConfirmationDialogService,
  ) {}

  async initialize(): Promise<void> {
    if (this.initialized || !this.isNativeAndroid()) {
      return;
    }

    this.initialized = true;
    const lifecycleVersion = this.lifecycleVersion;
    console.info(`${APP_UPDATE_LOG_SOURCE} Initializing Google Play update checks.`);
    try {
      await this.checkForUpdate();
      if (!this.initialized || lifecycleVersion !== this.lifecycleVersion) {
        return;
      }

      const appStateListener = await App.addListener('appStateChange', ({isActive}) => {
        this.handleAppStateChange(isActive);
      });
      if (!this.initialized || lifecycleVersion !== this.lifecycleVersion) {
        await appStateListener.remove();
        return;
      }

      this.appStateListener = appStateListener;
    } catch (error) {
      this.logFailure('Unable to initialize update checks.', error);
      if (lifecycleVersion === this.lifecycleVersion) {
        await this.appStateListener?.remove();
        this.appStateListener = undefined;
        this.initialized = false;
      }
    }
  }

  supportsManualCheck(): boolean {
    return this.isNativeAndroid();
  }

  async checkNow(): Promise<void> {
    if (!this.supportsManualCheck() || this.manualCheckInProgress) {
      return;
    }

    this.manualCheckInProgress = true;
    this.postponed = false;
    this.restartPromptPostponed = false;
    const lifecycleVersion = this.lifecycleVersion;
    const updateCheck = this.checkForUpdate();
    this.manualQuery = updateCheck;
    const appInfoPromise = this.getInstalledAppInfo();
    try {
      const state = await updateCheck;
      if (this.manualQuery === updateCheck) {
        this.manualQuery = undefined;
      }
      const appInfo = await appInfoPromise;
      if (lifecycleVersion !== this.lifecycleVersion) {
        return;
      }

      await this.showManualCheckDialog(state, appInfo, lifecycleVersion);
    } finally {
      if (this.manualQuery === updateCheck) {
        this.manualQuery = undefined;
      }
      this.manualCheckInProgress = false;
    }
  }

  async destroy(): Promise<void> {
    this.lifecycleVersion += 1;
    this.initialized = false;
    await this.stateListener?.remove();
    await this.appStateListener?.remove();
    await this.dismissActiveToast();
    this.stateListener = undefined;
    this.stateListenerRegistration = undefined;
    this.appStateListener = undefined;
    this.plugin = undefined;
    this.activeCheck = undefined;
    this.restartPrompt = undefined;
    this.postponed = false;
    this.restartPromptPostponed = false;
    this.updateFlowInProgress = false;
    this.updateStarted = false;
    this.clearDownloadProgress();
    this.downloadProgressFinished = false;
    this.manualCheckInProgress = false;
    this.manualQuery = undefined;
  }

  private isNativeAndroid(): boolean {
    return isPlatformBrowser(this.platformId)
      && !isStagingEnvironment()
      && Capacitor.isNativePlatform()
      && Capacitor.getPlatform() === 'android';
  }

  private handleAppStateChange(isActive: boolean): void {
    if (!isActive) {
      return;
    }

    this.restartPromptPostponed = false;
    void this.checkForUpdate();
  }

  private loadPlugin(): AppUpdatePlugin {
    return AppUpdate;
  }

  private connectPlugin(): AppUpdatePlugin {
    if (this.plugin) {
      this.registerStateListener(this.plugin);
      return this.plugin;
    }

    const plugin = this.loadPlugin();
    this.plugin = plugin;
    console.info(`${APP_UPDATE_LOG_SOURCE} Native update bridge connected.`);
    this.registerStateListener(plugin);
    return plugin;
  }

  private registerStateListener(plugin: AppUpdatePlugin): void {
    if (this.stateListener || this.stateListenerRegistration) {
      return;
    }

    const lifecycleVersion = this.lifecycleVersion;
    let listenerRegistration: Promise<PluginListenerHandle>;
    try {
      listenerRegistration = plugin.addListener('stateChange', state => {
        if (this.plugin !== plugin || lifecycleVersion !== this.lifecycleVersion) {
          return;
        }

        void this.ngZone.run(() => this.handleState(state))
          .catch(error => this.logFailure('Unable to handle an update state change.', error));
      });
    } catch (error) {
      this.logFailure('Unable to register the update state listener.', error);
      return;
    }

    const registration = listenerRegistration.then(async listener => {
      if (this.plugin !== plugin || lifecycleVersion !== this.lifecycleVersion) {
        await listener.remove();
        return;
      }

      this.stateListener = listener;
      console.info(`${APP_UPDATE_LOG_SOURCE} Update state listener registered.`);
    }).catch(error => {
      this.logFailure('Unable to register the update state listener.', error);
    }).finally(() => {
      if (this.stateListenerRegistration === registration) {
        this.stateListenerRegistration = undefined;
      }
    });

    this.stateListenerRegistration = registration;
  }

  private checkForUpdate(): Promise<AppUpdateState | undefined> {
    if (this.activeCheck) {
      return this.activeCheck;
    }

    const check = this.performUpdateCheck();
    this.activeCheck = check;
    void check.then(state => {
      if (state && this.manualQuery !== check) {
        void this.handleState(state)
          .catch(error => this.logFailure('Unable to handle the queried update state.', error));
      }
    });
    void check.finally(() => {
      if (this.activeCheck === check) {
        this.activeCheck = undefined;
      }
    });
    return check;
  }

  private async performUpdateCheck(): Promise<AppUpdateState | undefined> {
    const lifecycleVersion = this.lifecycleVersion;
    try {
      const plugin = this.connectPlugin();
      console.info(`${APP_UPDATE_LOG_SOURCE} Querying Google Play update state.`);
      const state = await this.withTimeout(
        plugin.getUpdateState(),
        UPDATE_CHECK_TIMEOUT_MS,
        'Google Play update state query timed out.',
      );
      console.info(`${APP_UPDATE_LOG_SOURCE} Google Play update state.`, state);
      if (lifecycleVersion !== this.lifecycleVersion) {
        return undefined;
      }

      return state;
    } catch (error) {
      this.logFailure('Google Play update check failed.', error);
      return undefined;
    }
  }

  private async withTimeout<T>(promise: Promise<T>, timeoutMs: number, message: string): Promise<T> {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => reject(new Error(message)), timeoutMs);
    });

    try {
      return await Promise.race([promise, timeout]);
    } finally {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    }
  }

  private async handleState(state: AppUpdateState): Promise<void> {
    switch (state.installStatus) {
      case 'pending':
      case 'downloading':
        if (this.downloadProgressFinished) {
          return;
        }
        this.updateStarted = true;
        await this.dismissActiveToast();
        await this.showDownloadProgress(state);
        return;
      case 'downloaded':
        this.finishDownloadProgress();
        this.updateStarted = true;
        if (!this.restartPromptPostponed) {
          await this.showRestartPrompt();
        }
        return;
      case 'installing':
        this.finishDownloadProgress();
        this.updateStarted = true;
        await this.showToast({message: this.translateService.instant('appUpdate.installing')});
        return;
      case 'failed':
        this.finishDownloadProgress();
        this.updateStarted = false;
        await this.showError();
        return;
      case 'canceled':
        this.finishDownloadProgress();
        this.updateStarted = false;
        await this.dismissActiveToast();
        return;
      case 'installed':
        this.finishDownloadProgress();
        this.updateStarted = false;
        await this.dismissActiveToast();
        return;
      default:
        break;
    }

    if (
      state.availability === 'available'
      && state.flexibleUpdateAllowed
      && !this.postponed
      && !this.updateStarted
    ) {
      await this.startFlexibleUpdate();
    }
  }

  private async startFlexibleUpdate(): Promise<void> {
    if (!this.plugin || this.updateFlowInProgress) {
      return;
    }

    this.updateFlowInProgress = true;
    await this.dismissActiveToast();
    this.clearDownloadProgress();
    this.downloadProgressFinished = false;
    try {
      const result = await this.plugin.startFlexibleUpdate();
      if (!result.accepted) {
        this.postponed = true;
        return;
      }

      this.updateStarted = true;
      this.showIndeterminateDownloadProgress();
      await this.checkForUpdate();
    } catch (error) {
      this.finishDownloadProgress();
      this.logFailure('Unable to start the flexible update flow.', error);
      await this.showError();
    } finally {
      this.updateFlowInProgress = false;
    }
  }

  private showDownloadProgress(state: AppUpdateState): void {
    const total = state.totalBytesToDownload ?? 0;
    const downloaded = state.bytesDownloaded ?? 0;
    const percentage = total > 0 ? Math.min(100, Math.round((downloaded / total) * 100)) : null;

    if (percentage === null) {
      this.showIndeterminateDownloadProgress();
      return;
    }

    const current = this.mutableDownloadProgress();
    if (current?.percentage !== null && current?.percentage !== undefined && percentage <= current.percentage) {
      return;
    }

    const announcedPercentage = this.shouldAnnounceDownloadProgress(current?.announcedPercentage, percentage)
      ? percentage
      : (current?.announcedPercentage ?? null);
    this.mutableDownloadProgress.set({percentage, announcedPercentage});
  }

  private showIndeterminateDownloadProgress(): void {
    if (this.downloadProgressFinished || this.mutableDownloadProgress()) {
      return;
    }

    this.mutableDownloadProgress.set({percentage: null, announcedPercentage: null});
  }

  private shouldAnnounceDownloadProgress(lastAnnounced: number | null | undefined, percentage: number): boolean {
    return lastAnnounced === null
      || lastAnnounced === undefined
      || percentage === 100
      || percentage - lastAnnounced >= DOWNLOAD_PROGRESS_ANNOUNCEMENT_STEP_PERCENT;
  }

  private finishDownloadProgress(): void {
    this.downloadProgressFinished = true;
    this.clearDownloadProgress();
  }

  private clearDownloadProgress(): void {
    this.mutableDownloadProgress.set(null);
  }

  private async showRestartPrompt(): Promise<void> {
    if (this.restartPrompt) {
      await this.restartPrompt;
      return;
    }

    const prompt = this.confirmationDialogService.open({
      title: this.translateService.instant('appUpdate.updateReadyTitle'),
      description: this.translateService.instant('appUpdate.ready'),
      cancelLabel: this.translateService.instant('appUpdate.later'),
      confirmLabel: this.translateService.instant('appUpdate.restart'),
      closeLabel: this.translateService.instant('appUpdate.later'),
      dialogType: 'information',
      confirmTone: 'primary',
      contentAlign: 'center',
      actionsAlign: 'center',
      panelVariant: 'notice',
      compactActions: true,
      heroIconName: 'information-circle-outline',
      showCloseButton: false,
      showCancelButton: true,
      onCancel: () => {
        this.restartPromptPostponed = true;
      },
      onConfirm: () => this.completeUpdate(),
    });
    this.restartPrompt = prompt;

    try {
      await prompt;
    } finally {
      if (this.restartPrompt === prompt) {
        this.restartPrompt = undefined;
      }
    }
  }

  private async completeUpdate(): Promise<void> {
    if (!this.plugin) {
      return;
    }

    await this.dismissActiveToast();
    try {
      await this.plugin.completeUpdate();
    } catch (error) {
      this.logFailure('Unable to complete the flexible update.', error);
      await this.showError();
    }
  }

  private async showError(): Promise<void> {
    await this.showToast({
      message: this.translateService.instant('appUpdate.error'),
      duration: 6000,
      buttons: [{text: this.translateService.instant('appUpdate.dismiss'), role: 'cancel'}],
    });
  }

  private async getInstalledAppInfo(): Promise<AppInfo | undefined> {
    try {
      return await this.withTimeout(
        this.loadInstalledAppInfo(),
        APP_INFO_TIMEOUT_MS,
        'Installed app version query timed out.',
      );
    } catch (error) {
      this.logFailure('Unable to read the installed app version.', error);
      return undefined;
    }
  }

  private loadInstalledAppInfo(): Promise<AppInfo> {
    return App.getInfo();
  }

  private async showManualCheckDialog(
    state: AppUpdateState | undefined,
    appInfo: AppInfo | undefined,
    lifecycleVersion: number,
  ): Promise<void> {
    await this.dismissActiveToast();
    if (lifecycleVersion !== this.lifecycleVersion) {
      return;
    }

    const version = appInfo?.version || this.translateService.instant('appUpdate.versionUnknown');
    const build = appInfo?.build || this.translateService.instant('appUpdate.versionUnknown');
    const common = {
      cancelLabel: this.translateService.instant('appUpdate.ok'),
      confirmLabel: this.translateService.instant('appUpdate.ok'),
      closeLabel: this.translateService.instant('appUpdate.ok'),
      dialogType: 'information' as const,
      confirmTone: 'primary' as const,
      contentAlign: 'center' as const,
      actionsAlign: 'center' as const,
      panelVariant: 'notice' as const,
      heroIconName: 'information-circle-outline',
      showCloseButton: false,
      showCancelButton: false,
    };

    if (!state) {
      await this.confirmationDialogService.open({
        ...common,
        title: this.translateService.instant('appUpdate.manualCheckFailedTitle'),
        description: this.translateService.instant('appUpdate.manualCheckFailedDescription', {version, build}),
      });
      return;
    }

    if (state.installStatus === 'failed') {
      await this.confirmationDialogService.open({
        ...common,
        title: this.translateService.instant('appUpdate.updateFailedTitle'),
        description: this.translateService.instant('appUpdate.updateFailedDescription', {version, build}),
      });
      return;
    }

    if (state.installStatus === 'downloaded') {
      await this.showRestartPrompt();
      return;
    }

    if (['pending', 'downloading', 'installing'].includes(state.installStatus)
      || state.availability === 'developerTriggeredUpdateInProgress') {
      await this.confirmationDialogService.open({
        ...common,
        title: this.translateService.instant('appUpdate.updateInProgressTitle'),
        description: this.translateService.instant('appUpdate.updateInProgressDescription', {version, build}),
      });
      return;
    }

    if (state.availability === 'unknown') {
      await this.confirmationDialogService.open({
        ...common,
        title: this.translateService.instant('appUpdate.manualCheckFailedTitle'),
        description: this.translateService.instant('appUpdate.manualCheckFailedDescription', {version, build}),
      });
      return;
    }

    if (state.availability === 'available') {
      const availableBuild = state.availableVersionCode
        ?? this.translateService.instant('appUpdate.versionUnknown');
      if (!state.flexibleUpdateAllowed) {
        await this.confirmationDialogService.open({
          ...common,
          title: this.translateService.instant('appUpdate.updateAvailableTitle'),
          description: this.translateService.instant('appUpdate.flexibleUpdateUnavailableDescription', {
            version,
            build,
            availableBuild,
          }),
          cancelLabel: this.translateService.instant('appUpdate.stayOnCurrentVersion'),
          confirmLabel: this.translateService.instant('appUpdate.openGooglePlay'),
          showCancelButton: true,
          onConfirm: () => this.openStoreListing(),
          errorFormatter: () => this.translateService.instant('appUpdate.openGooglePlayFailed'),
        });
        return;
      }

      await this.startFlexibleUpdate();
      return;
    }

    await this.confirmationDialogService.open({
      ...common,
      title: this.translateService.instant('appUpdate.upToDateTitle'),
      description: this.translateService.instant('appUpdate.upToDateDescription', {version, build}),
    });
  }

  private async showToast(options: ToastOptions): Promise<void> {
    const generation = ++this.toastGeneration;
    return this.enqueueToastOperation(async () => {
      if (generation !== this.toastGeneration) {
        return;
      }

      await this.dismissCurrentToast();
      if (generation !== this.toastGeneration) {
        return;
      }

      const toastPromise = this.toastController.create({
        ...options,
        duration: options.duration ?? 0,
        position: 'bottom',
        color: options.color ?? 'dark',
      });
      let toast: HTMLIonToastElement;
      try {
        toast = await this.withTimeout(
          toastPromise,
          TOAST_OPERATION_TIMEOUT_MS,
          'Toast creation timed out.',
        );
      } catch (error) {
        void toastPromise
          .then(staleToast => staleToast.dismiss().catch(() => undefined))
          .catch(() => undefined);
        throw error;
      }
      if (generation !== this.toastGeneration) {
        await this.dismissToast(toast, 'Unable to dismiss an obsolete update toast.');
        return;
      }

      this.activeToast = toast;
      let presentationAbandoned = false;
      const presentation = toast.present();
      void presentation.then(() => {
        if (presentationAbandoned) {
          void this.dismissToast(toast, 'Unable to dismiss a late update toast.');
        }
      }).catch(() => undefined);
      try {
        await this.withTimeout(
          presentation,
          TOAST_OPERATION_TIMEOUT_MS,
          'Toast presentation timed out.',
        );
      } catch (error) {
        presentationAbandoned = true;
        if (this.activeToast === toast) {
          this.activeToast = undefined;
        }
        await this.dismissToast(toast, 'Unable to dismiss an unresponsive update toast.');
        throw error;
      }

      if (generation !== this.toastGeneration) {
        if (this.activeToast === toast) {
          this.activeToast = undefined;
        }
        await this.dismissToast(toast, 'Unable to dismiss an obsolete update toast.');
      }
    });
  }

  private dismissActiveToast(): Promise<void> {
    this.toastGeneration += 1;
    return this.dismissCurrentToast();
  }

  private enqueueToastOperation(operation: () => Promise<void>): Promise<void> {
    const queuedOperation = this.toastOperation
      .catch(() => undefined)
      .then(operation);
    this.toastOperation = queuedOperation.catch(() => undefined);
    return queuedOperation;
  }

  private async dismissCurrentToast(): Promise<void> {
    const toast = this.activeToast;
    this.activeToast = undefined;
    if (toast) {
      await this.dismissToast(toast, 'Unable to dismiss the active update toast.');
    }
  }

  private async dismissToast(toast: HTMLIonToastElement, failureMessage: string): Promise<void> {
    await this.withTimeout(
      toast.dismiss(),
      TOAST_OPERATION_TIMEOUT_MS,
      'Toast dismissal timed out.',
    ).catch(error => this.logFailure(failureMessage, error));
  }

  private async openStoreListing(): Promise<void> {
    if (!this.plugin) {
      throw new Error('The Google Play update bridge is unavailable.');
    }

    await this.plugin.openStoreListing();
  }

  private logFailure(message: string, error: unknown): void {
    console.warn(`${APP_UPDATE_LOG_SOURCE} ${message}`, this.safeErrorDetails(error));
  }

  private safeErrorDetails(error: unknown): {name?: string; message?: string; code?: string | number} {
    if (!error || typeof error !== 'object') {
      return {message: String(error)};
    }

    const candidate = error as {name?: unknown; message?: unknown; code?: unknown};
    return {
      ...(typeof candidate.name === 'string' ? {name: candidate.name} : {}),
      ...(typeof candidate.message === 'string' ? {message: candidate.message} : {}),
      ...(typeof candidate.code === 'string' || typeof candidate.code === 'number'
        ? {code: candidate.code}
        : {}),
    };
  }
}
